import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import CopyRowButton from "../../components/alerts/CopyRowButton";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert as MuiAlert,
  TextField,
  Menu,
  ListItemText,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Badge,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CopyableValue from '../../components/common/CopyableValue';
import CopyableCardNumber from '../../components/alerts/CopyableCardNumber';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { SUBSCRIPTION_STATUS_FILTER_OPTIONS, SUBSCRIPTION_STATUS_FILTER_VALUES, RDR_CASE_EVENT_FILTER_OPTIONS, RDR_CASE_EVENT_FILTER_VALUES, OUTCOME_FILTER_OPTIONS, OUTCOME_FILTER_VALUES, AlertSubscriptionStatus, AlertSubscriptionStatusLabel, RefundedExternallyLabel, REFUNDED_EXTERNALLY_OPTIONS, REFUNDED_EXTERNALLY_FILTER_OPTIONS, REFUNDED_EXTERNALLY_FILTER_VALUES, formatVerifiStatusCode, formatVerifiReasonCode, FulfillmentStatusLabel } from "../../types/alerts";
import MultiSelectFilter from "../../components/common/MultiSelectFilter";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import { filterPanelSx } from "../../components/common/filterStyles";
import {
  VerifiNotice,
  VerifiNoticesResponse,
  VerifiNoticeMerchantFilterItem,
  VerifiNoticeDescriptorFilterItem,
} from "../../types/verifiNotice";
import { useCompanyId } from "../../hooks/useCompanyId";
import { HasPermission } from "../../functions/auth";
import { useStateValue } from "../../context/StateValueContext";
import { hasPermission } from "../../functions/permissionUtils";
import { Agent } from "../../types/user";
import { PERMISSIONS } from "../../constants/permissions";
import { formatCurrency, formatAmountWithoutCurrency } from "../../utils/formatCurrency";
import ActivityLog from "./ActivityLog";
import * as Sentry from "@sentry/react";
import VerifiNoticesCsvDialog from "../../components/alerts/VerifiNoticesCsvDialog";
import MapMerchantToCompanyDialog, { RDR_PROVIDER_CONFIG, useUnassignedMerchants } from "../../components/alerts/MapMerchantToCompanyDialog";
import { pascalCase } from "../../utils/textUtils";
import AgeChip from "../../components/alerts/AgeChip";
import SubscriptionStatusChip from "../../components/alerts/SubscriptionStatusChip";
import RdrResolutionDialog from "../../components/alerts/RdrResolutionDialog";
import FulfillmentStatusSelect from "../../components/alerts/FulfillmentStatusSelect";
import FulfillmentStatusDialog from "../../components/alerts/FulfillmentStatusDialog";
import MappingWarningChip from "../../components/alerts/MappingWarningChip";
import PreMatchedChip from "../../components/alerts/PreMatchedChip";
import { formatAgeLabel } from "../../utils/formatAge";
import { companySearchText } from "../../utils/companyUtils";

// Subscription Status filter options (replaces the legacy CM Status tabs)

// Column configuration
const COLUMNS_STORAGE_KEY = 'rdr_notices_visible_columns_v5';

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
  locked?: boolean;
  sortField?: string;
}

const allColumns: ColumnConfig[] = [
  { id: 'case_id', label: 'Case ID', isDefault: false },
  { id: 'subscription_status', label: 'Subscription Status', isDefault: true, locked: true },
  { id: 'company_name', label: 'Company', isDefault: false },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'refunded_externally', label: 'Refunded Externally', isDefault: false },
  { id: 'fulfillment_status', label: 'Fulfillment', isDefault: false },
  { id: 'case_event', label: 'Case Event', isDefault: true },
  { id: 'merchant_name', label: 'Merchant', isDefault: false },
  { id: 'payment_descriptor', label: 'Descriptor', isDefault: true },
  { id: 'case_date', label: 'Case Date', isDefault: true, sortField: 'case_date' },
  { id: 'age', label: 'Age', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'card_number', label: 'Card Number', isDefault: true },
  { id: 'auth_code', label: 'Auth Code', isDefault: true },
  { id: 'transaction_id', label: 'Transaction ID', isDefault: true },
  { id: 'outcome', label: 'Outcome', isDefault: false },
  { id: 'billable', label: 'Billable', isDefault: false },
  { id: 'billed_at', label: 'Billed At', isDefault: false },
  { id: 'acquirer_bin', label: 'Acquirer BIN', isDefault: false },
  { id: 'arn', label: 'ARN', isDefault: false },
  { id: 'card_acceptor_id', label: 'Card Acceptor ID', isDefault: false },
  { id: 'reason_code', label: 'Reason', isDefault: false },
  { id: 'status_code', label: 'Status', isDefault: false },
  { id: 'transaction_date', label: 'Transaction Date', isDefault: false },
  { id: 'cm_notes', label: 'Notes', isDefault: false },
  { id: 'matched_order_url', label: 'Order URL', isDefault: false },
  { id: 'matched_gateway_title', label: 'Matched Gateway', isDefault: false },
  { id: 'google_sheet_mid', label: 'Sheets MID', isDefault: false },
  { id: 'resolved_at', label: 'Resolved At', isDefault: false },
  { id: 'warnings', label: 'Warnings', isDefault: false },
  { id: 'assigned_user', label: 'Assigned To', isDefault: true },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '--';
  return dayjs.utc(dateString).format('MMM DD, YYYY');
};

// Queued-for-automation notices can't be assigned to a user.
const isNoticeAssignable = (notice: VerifiNotice): boolean => {
  return notice.subscription_status !== AlertSubscriptionStatus.QUEUED;
};

function RdrNoticeRow({
  notice, onResolve, visibleColumns, selectionEnabled, selected, onToggleSelect
}: {
  notice: VerifiNotice;
  onResolve: (success: boolean, message?: string) => void;
  visibleColumns: string[];
  selectionEnabled: boolean;
  selected: boolean;
  onToggleSelect: (noticeId: number) => void;
}) {
  const { post, patch, loading, error } = useApi();
  const [open, setOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveSubscriptionStatus, setResolveSubscriptionStatus] = useState<string>('');
  const [resolveRefundedExternally, setResolveRefundedExternally] = useState<string>('');
  const [resolveFulfillmentStatus, setResolveFulfillmentStatus] = useState<string>('');
  const [resolveOrderUrl, setResolveOrderUrl] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [editNotes, setEditNotes] = useState<string>('');
  const [subscriptionStatusDialogOpen, setSubscriptionStatusDialogOpen] = useState(false);
  const [fulfillmentStatusDialogOpen, setFulfillmentStatusDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenResolveDialog = () => {
    handleCloseMenu();
    setResolveDialogOpen(true);
    setResolveSubscriptionStatus('');
    setResolveRefundedExternally(notice.refunded_externally ?? '');
    setResolveFulfillmentStatus(notice.fulfillment_status ?? '');
    setNotes('');
    setResolveOrderUrl(notice.matched_order_url || '');
  };

  const handleOpenSubscriptionStatusDialog = () => {
    handleCloseMenu();
    setSubscriptionStatusDialogOpen(true);
  };

  const handleCloseSubscriptionStatusDialog = () => {
    const shouldRefresh = !!error;
    setSubscriptionStatusDialogOpen(false);
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleSubscriptionStatusSaved = () => {
    setSubscriptionStatusDialogOpen(false);
    onResolve(true, 'Subscription status updated');
  };

  const handleOpenFulfillmentStatusDialog = () => {
    handleCloseMenu();
    setFulfillmentStatusDialogOpen(true);
  };

  const handleCloseFulfillmentStatusDialog = () => {
    setFulfillmentStatusDialogOpen(false);
  };

  const handleFulfillmentStatusSaved = () => {
    setFulfillmentStatusDialogOpen(false);
    onResolve(true, 'Fulfillment status updated');
  };

  const handleFindPayment = () => {
    handleCloseMenu();
    const params = new URLSearchParams();
    if (notice.transaction_date) {
      const txnDate = dayjs.utc(notice.transaction_date);
      params.set('startDate', txnDate.subtract(1, 'day').format('YYYY-MM-DD'));
      params.set('endDate', txnDate.add(1, 'day').format('YYYY-MM-DD'));
    }
    if (notice.card_bin) params.set('cardBin', notice.card_bin);
    if (notice.card_last4) params.set('cardLast4', notice.card_last4);
    if (notice.metadata?.authCode) params.set('gwAuthCode', notice.metadata.authCode);
    if (notice.metadata?.transactionId) params.set('gwTransactionId', notice.metadata.transactionId);
    if (notice.pp_type) params.set('ppType', notice.pp_type);
    if (notice.pp_account_id) params.set('ppAccountId', notice.pp_account_id.toString());
    if (notice.company_id) params.set('companyId', notice.company_id.toString());
    window.open(`/payment-search?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleCloseResolveDialog = () => {
    const shouldRefresh = !!error;
    setResolveDialogOpen(false);
    setResolveSubscriptionStatus('');
    setResolveRefundedExternally('');
    setResolveFulfillmentStatus('');
    setNotes('');
    setResolveOrderUrl('');
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleResolveDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    handleCloseResolveDialog();
  };

  const handleResolveSubmit = async () => {
    if (resolveSubscriptionStatus && resolveRefundedExternally && resolveFulfillmentStatus && !loading) {
      try {
        await post(`/api/alerts/verifi-notices/resolution`, {
          noticeId: notice.verifi_notice_id,
          subscriptionStatus: resolveSubscriptionStatus,
          refundedExternally: resolveRefundedExternally,
          fulfillmentStatus: resolveFulfillmentStatus,
          notes: notes || undefined,
          orderUrl: resolveOrderUrl.trim() || undefined,
        });
        handleCloseResolveDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to resolve RDR notice:', err);
      }
    }
  };

  const handleOpenEditNotesDialog = () => {
    handleCloseMenu();
    setEditNotesDialogOpen(true);
    setEditNotes(notice.cm_notes || '');
  };

  const handleCloseEditNotesDialog = () => {
    const shouldRefresh = !!error;
    setEditNotesDialogOpen(false);
    setEditNotes('');
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleEditNotesDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') return;
    handleCloseEditNotesDialog();
  };

  const handleEditNotesSubmit = async () => {
    if (editNotes.trim() && !loading) {
      try {
        await patch(`/api/alerts/verifi-notices/notes`, {
          noticeId: notice.verifi_notice_id,
          notes: editNotes.trim(),
        });
        handleCloseEditNotesDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to edit notes:', err);
      }
    }
  };

  const canBeResolvedManually = notice.subscription_status === AlertSubscriptionStatus.ACTION_NEEDED;

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'assigned_user':
        return (
          <TableCell key={columnId}>
            {notice.assigned_user_id ? (
              <Tooltip title={notice.assigned_email ?? ''} placement="top-end" arrow>
                <span>{notice.assigned_first_name ?? notice.assigned_email}</span>
              </Tooltip>
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'subscription_status':
        return (
          <TableCell key={columnId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubscriptionStatusChip status={notice.subscription_status} />
              {notice.matched_order_url && canBeResolvedManually && <PreMatchedChip />}
              {!notice.company_id && (
                <Tooltip title="Not linked to any company" arrow>
                  <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: '20px' }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
        );
      case 'refunded_externally':
        return (
          <TableCell key={columnId}>
            {notice.refunded_externally ? RefundedExternallyLabel[notice.refunded_externally] : '--'}
          </TableCell>
        );
      case 'fulfillment_status':
        return <TableCell key={columnId}>{notice.fulfillment_status ? FulfillmentStatusLabel[notice.fulfillment_status] : '--'}</TableCell>;
      case 'case_event':
        return <TableCell key={columnId}>{notice.case_event ? pascalCase(notice.case_event) : '--'}</TableCell>;
      case 'merchant_name':
        return <TableCell key={columnId}>{notice.merchant_name || '--'}</TableCell>;
      case 'payment_descriptor':
        return <TableCell key={columnId}>{notice.payment_descriptor || '--'}</TableCell>;
      case 'case_date':
        return <TableCell key={columnId}>{formatDateTime(notice.case_date)}</TableCell>;
      case 'age': {
        const isNonSubscription = notice.subscription_status === AlertSubscriptionStatus.NOT_SUBSCRIPTION
          || notice.subscription_status === AlertSubscriptionStatus.AUTO_NOT_SUBSCRIPTION;
        return (
          <TableCell key={columnId}>
            <AgeChip
              ageHours={notice.age_hours}
              expiredThreshold={isNonSubscription ? Infinity : 72}
              dangerThreshold={isNonSubscription ? Infinity : 15}
              warningThreshold={isNonSubscription ? Infinity : 12}
            />
          </TableCell>
        );
      }
      case 'amount': {
        const cents = notice.transaction_amount;
        if (cents === null || cents === undefined) return <TableCell key={columnId}>--</TableCell>;
        return (
          <TableCell key={columnId}>
            <CopyableValue
              text={formatCurrency(cents, notice.transaction_currency || 'USD', true)}
              copyValue={formatAmountWithoutCurrency(cents, notice.transaction_currency || 'USD')}
              copyTitle="Copy amount"
              revealOnHover
            />
          </TableCell>
        );
      }
      case 'card_number':
        return (
          <TableCell key={columnId}>
            <CopyableCardNumber cardBin={notice.card_bin} cardLast4={notice.card_last4} revealOnHover />
          </TableCell>
        );
      case 'auth_code':
        return (
          <TableCell key={columnId}>
            {notice.metadata?.authCode ? (
              <CopyableValue
                text={String(notice.metadata.authCode)}
                copyTitle="Copy auth code"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'transaction_id':
        return (
          <TableCell key={columnId}>
            {notice.metadata?.transactionId ? (
              <CopyableValue
                text={String(notice.metadata.transactionId)}
                copyTitle="Copy transaction ID"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'outcome':
        return <TableCell key={columnId}>{notice.outcome ? pascalCase(notice.outcome) : '--'}</TableCell>;
      case 'billable':
        return <TableCell key={columnId}>{notice.billable ? 'Yes' : 'No'}</TableCell>;
      case 'billed_at':
        return <TableCell key={columnId}>{formatDateTime(notice.billed_at)}</TableCell>;
      case 'case_id':
        return (
          <TableCell key={columnId}>
            {notice.case_id ? (
              <CopyableValue
                text={notice.case_id}
                copyTitle="Copy case ID"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'company_name':
        return <TableCell key={columnId}>{notice.company_name || '--'}</TableCell>;
      case 'brand_name':
        return <TableCell key={columnId}>{notice.brand_name || '--'}</TableCell>;
      case 'acquirer_bin':
        return <TableCell key={columnId}>{notice.acquirer_bin || '--'}</TableCell>;
      case 'arn':
        return (
          <TableCell key={columnId}>
            {notice.metadata?.arn ? (
              <CopyableValue
                text={String(notice.metadata.arn)}
                copyTitle="Copy ARN"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'card_acceptor_id':
        return <TableCell key={columnId}>{notice.card_acceptor_id || '--'}</TableCell>;
      case 'reason_code':
        return <TableCell key={columnId}>{formatVerifiReasonCode(notice.reason_code)}</TableCell>;
      case 'status_code':
        return <TableCell key={columnId}>{formatVerifiStatusCode(notice.status_code)}</TableCell>;
      case 'transaction_date':
        return (
          <TableCell key={columnId}>
            {notice.transaction_date ? (
              <CopyableValue
                text={formatDate(notice.transaction_date)}
                copyTitle="Copy transaction date"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'cm_notes':
        return <TableCell key={columnId}>{notice.cm_notes || '--'}</TableCell>;
      case 'matched_order_url':
        return (
          <TableCell key={columnId}>
            {notice.matched_order_url ? (
              <a href={notice.matched_order_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#465fff' }}>
                Open <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </a>
            ) : '--'}
          </TableCell>
        );
      case 'matched_gateway_title':
        return <TableCell key={columnId}>{notice.matched_gateway_title || '--'}</TableCell>;
      case 'google_sheet_mid':
        return <TableCell key={columnId}>{notice.google_sheet_mid || '--'}</TableCell>;
      case 'resolved_at':
        return <TableCell key={columnId}>{formatDateTime(notice.subscription_status_changed_at)}</TableCell>;
      case 'warnings': {
        const missingCompany = !notice.company_id;
        const missingPp = !notice.pp_account_id;
        if (!missingCompany && !missingPp) {
          return <TableCell key={columnId}></TableCell>;
        }
        return (
          <TableCell key={columnId}>
            <Typography variant="body2" sx={{ color: '#ed6c02', whiteSpace: 'nowrap' }}>
              {missingCompany ? 'No company' : 'No PP Account'}
            </Typography>
          </TableCell>
        );
      }
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow key={notice.verifi_notice_id} hover onClick={() => setOpen(!open)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}>
        <TableCell padding="checkbox" sx={{ width: 48, textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
          {selectionEnabled && (
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelect(notice.verifi_notice_id)}
              disabled={!isNoticeAssignable(notice)}
              size="small"
            />
          )}
        </TableCell>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <TableCell>
          <IconButton
            aria-label="more actions"
            aria-controls={menuOpen ? `notice-menu-${notice.verifi_notice_id}` : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleOpenMenu}
            size="small"
            sx={{ padding: '4px' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id={`notice-menu-${notice.verifi_notice_id}`}
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {canBeResolvedManually && (
              <MenuItem onClick={handleOpenResolveDialog}>
                <ListItemText>Resolve</ListItemText>
              </MenuItem>
            )}
            {!canBeResolvedManually && (
              <MenuItem onClick={handleOpenSubscriptionStatusDialog}>
                <ListItemText>Edit Resolution</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleFindPayment}>
              <ListItemText>Find Payment</ListItemText>
            </MenuItem>
            {notice.cm_notes && (
              <MenuItem onClick={handleOpenEditNotesDialog}>
                <ListItemText>Edit Notes</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenFulfillmentStatusDialog}>
              <ListItemText>Edit Fulfillment Status</ListItemText>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      {/* Resolution Dialog */}
      {subscriptionStatusDialogOpen && (
        <RdrResolutionDialog
          open={subscriptionStatusDialogOpen}
          notice={notice}
          onClose={handleCloseSubscriptionStatusDialog}
          onSave={handleSubscriptionStatusSaved}
        />
      )}
      {fulfillmentStatusDialogOpen && (
        <FulfillmentStatusDialog
          open={fulfillmentStatusDialogOpen}
          fulfillmentStatusApiUrl="/api/alerts/verifi-notices/fulfillment-status"
          alertId={notice.verifi_notice_id}
          alertLabel={notice.case_id}
          currentStatus={notice.fulfillment_status}
          onClose={handleCloseFulfillmentStatusDialog}
          onSave={handleFulfillmentStatusSaved}
        />
      )}
      {/* Edit Notes Dialog */}
      <Dialog
        open={editNotesDialogOpen}
        onClose={handleEditNotesDialogClose}
        aria-labelledby="edit-notes-dialog-title"
      >
        <DialogTitle id="edit-notes-dialog-title">
          Edit Notes — Notice #{notice.case_id}
        </DialogTitle>
        <DialogContent>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          <TextField
            size="small"
            fullWidth
            label="Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditNotesDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditNotesSubmit}
            variant="contained"
            color="primary"
            disabled={!editNotes.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleResolveDialogClose}
        aria-labelledby="resolve-notice-dialog-title"
        aria-describedby="resolve-notice-dialog-description"
      >
        <DialogTitle id="resolve-notice-dialog-title">
          Resolve RDR Notice #{notice.case_id}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resolve-notice-dialog-description" sx={{ mb: 3 }}>
            Set the subscription status for this RDR notice.
          </DialogContentText>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }} required disabled={loading}>
                <InputLabel id="resolve-subscription-status-label" shrink>Subscription Status</InputLabel>
                <Select
                  labelId="resolve-subscription-status-label"
                  id="resolve-subscription-status"
                  value={resolveSubscriptionStatus}
                  label="Subscription Status"
                  displayEmpty
                  notched
                  onChange={(e) => setResolveSubscriptionStatus(e.target.value)}
                >
                  <MenuItem value={AlertSubscriptionStatus.CANCELLED}>
                    {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.CANCELLED]}
                  </MenuItem>
                  <MenuItem value={AlertSubscriptionStatus.NOT_SUBSCRIPTION}>
                    {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.NOT_SUBSCRIPTION]}
                  </MenuItem>
                  <MenuItem value={AlertSubscriptionStatus.NOT_FOUND}>
                    {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.NOT_FOUND]}
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }} required disabled={loading}>
                <InputLabel id="resolve-refunded-externally-label" shrink>Refunded Externally</InputLabel>
                <Select
                  labelId="resolve-refunded-externally-label"
                  id="resolve-refunded-externally"
                  value={resolveRefundedExternally}
                  label="Refunded Externally"
                  displayEmpty
                  notched
                  onChange={(e) => setResolveRefundedExternally(e.target.value)}
                >
                  {REFUNDED_EXTERNALLY_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>{RefundedExternallyLabel[v]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FulfillmentStatusSelect
              value={resolveFulfillmentStatus}
              onChange={setResolveFulfillmentStatus}
              disabled={loading}
            />
            <TextField
              size="small"
              fullWidth
              label="Order URL"
              value={resolveOrderUrl}
              onChange={(e) => setResolveOrderUrl(e.target.value)}
              placeholder="https://..."
              helperText="Shopify: https://<shop>.myshopify.com/admin/orders/<id> (admin.shopify.com links are converted). CheckoutChamp: https://crm.checkoutchamp.com/customer/cs/orders/?orderId=<id> (customer page links are rejected). This field is for tracking purposes only and does not mean the order will be refunded automatically."
              disabled={loading}
            />
            <TextField
              size="small"
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseResolveDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveSubmit}
            variant="contained"
            color="primary"
            autoFocus
            disabled={!resolveSubscriptionStatus || !resolveRefundedExternally || !resolveFulfillmentStatus || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notice Details
                </Typography>
                <CopyRowButton row={notice} columnSet="verifi-notice" />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, maxWidth: 1200 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case ID</Typography>
                  {notice.case_id ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={notice.case_id}
                        copyTitle="Copy case ID"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Subscription Status</Typography>
                  <SubscriptionStatusChip status={notice.subscription_status} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case Event</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.case_event ? pascalCase(notice.case_event) : '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Outcome</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.outcome ? pascalCase(notice.outcome) : '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Billable</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.billable ? 'Yes' : 'No'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Refunded Externally</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.refunded_externally ? RefundedExternallyLabel[notice.refunded_externally] : '--'}</Typography>
                </Box>
                {notice.fulfillment_status && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Fulfillment Status</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{FulfillmentStatusLabel[notice.fulfillment_status]}</Typography>
                  </Box>
                )}
                {notice.matched_order_url && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order URL
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={notice.matched_order_url}
                        href={notice.matched_order_url}
                        copyTitle="Copy order URL"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                {notice.matched_gateway_title && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Matched Gateway</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.matched_gateway_title}</Typography>
                  </Box>
                )}
                {notice.google_sheet_mid && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Sheets MID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.google_sheet_mid}</Typography>
                  </Box>
                )}
                {notice.reason_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Reason</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatVerifiReasonCode(notice.reason_code)}</Typography>
                  </Box>
                )}
                {notice.status_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatVerifiStatusCode(notice.status_code)}</Typography>
                  </Box>
                )}
                {notice.cm_notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.cm_notes}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Company</Typography>
                  {notice.company_name ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.company_name}</Typography>
                  ) : (
                    <MappingWarningChip label="Not linked to any company" />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">PP Account</Typography>
                  {notice.pp_account_id ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {notice.pp_type ? `${notice.pp_type} / ` : ''}{notice.pp_account_id}
                    </Typography>
                  ) : (
                    <MappingWarningChip label="No PP Account" />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Merchant</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.merchant_name || '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Descriptor</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.payment_descriptor || '--'}</Typography>
                </Box>
                {notice.acquirer_bin && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Acquirer BIN</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.acquirer_bin}</Typography>
                  </Box>
                )}
                {notice.card_acceptor_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Card Acceptor ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.card_acceptor_id}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  {notice.transaction_amount === null || notice.transaction_amount === undefined ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  ) : (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={formatCurrency(notice.transaction_amount, notice.transaction_currency || 'USD', true)}
                        copyValue={formatAmountWithoutCurrency(notice.transaction_amount, notice.transaction_currency || 'USD')}
                        copyTitle="Copy amount"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Card Number</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <CopyableCardNumber
                      cardBin={notice.card_bin}
                      cardLast4={notice.card_last4}
                      textSx={{ fontSize: '1rem', fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                {notice.metadata?.authCode && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Auth Code</Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={String(notice.metadata.authCode)}
                        copyTitle="Copy auth code"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                {notice.metadata?.arn && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">ARN</Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={String(notice.metadata.arn)}
                        copyTitle="Copy ARN"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                {notice.metadata?.transactionId && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Transaction ID</Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={String(notice.metadata.transactionId)}
                        copyTitle="Copy transaction ID"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Transaction Date</Typography>
                  {notice.transaction_date ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={formatDate(notice.transaction_date)}
                        copyTitle="Copy transaction date"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.case_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Age</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.age_hours != null ? formatAgeLabel(notice.age_hours) : '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.created_at)}</Typography>
                </Box>
              </Box>

              <ActivityLog logs={notice.logs} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function RdrNotices() {
  const { get, post, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [{ user, agents, miniCompanies }, dispatch] = useStateValue();
  const hasAssignPermission = useMemo(() => hasPermission(user, PERMISSIONS.ALERTS_ASSIGN), [user]);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Read initial values from URL or use defaults
  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const subStatusParam = urlSearchParams.get("subStatus");
  const initialSubscriptionStatuses = subStatusParam === null
    ? ['action_needed']
    : subStatusParam.split(',').filter(v => SUBSCRIPTION_STATUS_FILTER_VALUES.includes(v));
  const initialRefundedExternally = (urlSearchParams.get("refundedExternally") || '').split(',').filter(v => REFUNDED_EXTERNALLY_FILTER_VALUES.includes(v));
  const initialCaseId = urlSearchParams.get("caseId") || '';
  const initialNoCompany = urlSearchParams.get("noCompany") === 'true';
  const initialBillableOnly = urlSearchParams.get("billableOnly") === 'true';
  const initialUnbilled = urlSearchParams.get("unbilled") === 'true';
  const initialPage = urlSearchParams.get("page") ? Math.max(0, parseInt(urlSearchParams.get("page")!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get("rowsPerPage") ? parseInt(urlSearchParams.get("rowsPerPage")!, 10) : 25;
  const initialSortBy = urlSearchParams.get("sortBy") || 'case_date';
  const initialSortOrder = (urlSearchParams.get("sortOrder") === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';
  const initialAssignedUserFilter = (urlSearchParams.get("userIds") || '').split(',').filter(Boolean);
  const initialCaseEvents = (urlSearchParams.get("caseEvent") || '').split(',').filter(v => RDR_CASE_EVENT_FILTER_VALUES.includes(v));
  const initialOutcomes = (urlSearchParams.get("outcome") || '').split(',').filter(v => OUTCOME_FILTER_VALUES.includes(v));
  const initialMerchantIds = (urlSearchParams.get("merchantIds") || '').split(',').filter(Boolean);
  const initialCompanyIds = (urlSearchParams.get("companyIds") || '').split(',').filter(Boolean);
  const initialDescriptorIds = (urlSearchParams.get("descriptorIds") || '').split(',').filter(Boolean);

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [caseId, setCaseId] = useState<string>(initialCaseId);
  const [noCompany, setNoCompany] = useState(initialNoCompany);
  const [billableOnly, setBillableOnly] = useState(initialBillableOnly);
  const [unbilled, setUnbilled] = useState(initialUnbilled);
  const [subscriptionStatuses, setSubscriptionStatuses] = useState<string[]>(initialSubscriptionStatuses);
  const [refundedExternally, setRefundedExternally] = useState<string[]>(initialRefundedExternally);
  const [caseEvents, setCaseEvents] = useState<string[]>(initialCaseEvents);
  const [outcomes, setOutcomes] = useState<string[]>(initialOutcomes);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [companyIds, setCompanyIds] = useState<string[]>(initialCompanyIds);
  const [descriptorIds, setDescriptorIds] = useState<string[]>(initialDescriptorIds);
  const [noticeMerchants, setNoticeMerchants] = useState<VerifiNoticeMerchantFilterItem[]>([]);
  const [noticeDescriptors, setNoticeDescriptors] = useState<VerifiNoticeDescriptorFilterItem[]>([]);
  const [noticesData, setNoticesData] = useState<VerifiNoticesResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [pageError, setPageError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Assignment + multi-select
  const [assignedUserFilter, setAssignedUserFilter] = useState<string[]>(initialAssignedUserFilter);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState<Set<number>>(new Set());
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignAgent, setBulkAssignAgent] = useState<number | null>(null);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [mapMerchantDialogOpen, setMapMerchantDialogOpen] = useState(false);
  const { merchants: unmappedMerchants, refresh: refreshUnmappedMerchants } = useUnassignedMerchants(RDR_PROVIDER_CONFIG);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  // Filter row collapse
  const hasActiveFilters = caseId !== '' || noCompany || caseEvents.length > 0 || outcomes.length > 0 || merchantIds.length > 0 || companyIds.length > 0 || descriptorIds.length > 0;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount = (caseId !== '' ? 1 : 0) + (noCompany ? 1 : 0) + (caseEvents.length > 0 ? 1 : 0) + (outcomes.length > 0 ? 1 : 0) + (merchantIds.length > 0 ? 1 : 0) + (companyIds.length > 0 ? 1 : 0) + (descriptorIds.length > 0 ? 1 : 0);

  const assignedUserFilterOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'assigned', label: 'Assigned' },
    ...agents.map((agent) => ({ value: String(agent.userId), label: `${agent.name} (${agent.email})` })),
  ];
  const merchantOptions = noticeMerchants.map((merchant) => ({ value: String(merchant.merchant_id), label: merchant.merchant_name }));
  const companyOptions = miniCompanies.map((c) => ({ value: String(c.id), label: c.name, searchText: companySearchText(c) }));
  const descriptorOptions = noticeDescriptors.map((desc) => ({ value: String(desc.descriptor_id), label: desc.descriptor_name }));

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const handleShowAllColumns = () => {
    setVisibleColumns(validColumnIds);
    saveStoredColumns(validColumnIds);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Update URL whenever search parameters change
  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) {
      params.set("startDate", startDate.format("YYYY-MM-DD"));
    } else {
      params.delete("startDate");
    }

    if (endDate) {
      params.set("endDate", endDate.format("YYYY-MM-DD"));
    } else {
      params.delete("endDate");
    }

    if (caseId) {
      params.set("caseId", caseId);
    } else {
      params.delete("caseId");
    }

    if (noCompany) {
      params.set("noCompany", "true");
    } else {
      params.delete("noCompany");
    }

    params.set("subStatus", subscriptionStatuses.join(','));

    if (refundedExternally.length > 0) {
      params.set("refundedExternally", refundedExternally.join(','));
    } else {
      params.delete("refundedExternally");
    }

    if (page > 0) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }

    if (rowsPerPage > 0) {
      params.set("rowsPerPage", rowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }

    if (sortBy && sortBy !== 'case_date') {
      params.set("sortBy", sortBy);
    } else {
      params.delete("sortBy");
    }

    if (sortOrder !== 'asc') {
      params.set("sortOrder", sortOrder);
    } else {
      params.delete("sortOrder");
    }

    if (assignedUserFilter.length > 0) {
      params.set("userIds", assignedUserFilter.join(','));
    } else {
      params.delete("userIds");
    }

    if (caseEvents.length > 0) {
      params.set("caseEvent", caseEvents.join(','));
    } else {
      params.delete("caseEvent");
    }

    if (outcomes.length > 0) {
      params.set("outcome", outcomes.join(','));
    } else {
      params.delete("outcome");
    }

    if (merchantIds.length > 0) {
      params.set("merchantIds", merchantIds.join(','));
    } else {
      params.delete("merchantIds");
    }

    if (companyIds.length > 0) {
      params.set("companyIds", companyIds.join(','));
    } else {
      params.delete("companyIds");
    }

    if (descriptorIds.length > 0) {
      params.set("descriptorIds", descriptorIds.join(','));
    } else {
      params.delete("descriptorIds");
    }

    if (billableOnly) {
      params.set("billableOnly", "true");
    } else {
      params.delete("billableOnly");
    }

    if (unbilled) {
      params.set("unbilled", "true");
    } else {
      params.delete("unbilled");
    }

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, caseId, noCompany, subscriptionStatuses, refundedExternally, caseEvents, outcomes, merchantIds, companyIds, descriptorIds, billableOnly, unbilled, page, rowsPerPage, sortBy, sortOrder, assignedUserFilter]);

  const fetchNoticeMerchants = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ merchants: VerifiNoticeMerchantFilterItem[] }>(`/api/alerts/verifi-notices/merchants${params}`);
      setNoticeMerchants(response.merchants);
    } catch (err) {
      console.error('Failed to fetch RDR merchants:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchNoticeDescriptors = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ descriptors: VerifiNoticeDescriptorFilterItem[] }>(`/api/alerts/verifi-notices/descriptors${params}`);
      setNoticeDescriptors(response.descriptors);
    } catch (err) {
      console.error('Failed to fetch RDR descriptors:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchNoticeMerchants();
  }, [fetchNoticeMerchants]);

  useEffect(() => {
    fetchNoticeDescriptors();
  }, [fetchNoticeDescriptors]);

  const fetchNotices = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    setPageError(null);

    try {
      const companyIdsParam = companyIds.length > 0 ? companyIds.join(',') : (companyId ? companyId.toString() : '');

      const params = new URLSearchParams({
        caseType: 'RDR_NOTICE',
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        ...(caseId && { caseId }),
        ...(noCompany && { noCompany: 'true' }),
        ...(subscriptionStatuses.length > 0 && { subscriptionStatuses: subscriptionStatuses.join(',') }),
        ...(refundedExternally.length > 0 && { refundedExternally: refundedExternally.join(',') }),
        ...(caseEvents.length > 0 && { caseEvent: caseEvents.join(',') }),
        ...(outcomes.length > 0 && { outcome: outcomes.join(',') }),
        ...(merchantIds.length > 0 && { merchantIds: merchantIds.join(',') }),
        ...(companyIdsParam && { companyIds: companyIdsParam }),
        ...(descriptorIds.length > 0 && { descriptorIds: descriptorIds.join(',') }),
        ...(billableOnly && { billableOnly: 'true' }),
        ...(unbilled && { unbilled: 'true' }),
        ...(assignedUserFilter.length > 0 && { userIds: assignedUserFilter.join(',') }),
        ...(brandId && { brandId: String(brandId) }),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      const response = await get<VerifiNoticesResponse>(`/api/alerts/verifi-notices?${params.toString()}`);
      setNoticesData(response);
    } catch (err) {
      console.error('Failed to fetch RDR notices:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setNoticesData(null);
    }
  }, [get, startDate, endDate, caseId, noCompany, subscriptionStatuses, refundedExternally, caseEvents, outcomes, merchantIds, companyIds, descriptorIds, billableOnly, unbilled, companyId, brandId, assignedUserFilter, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  useEffect(() => {
    if (!user || agents.length > 0) return;
    const fetchAgents = async () => {
      try {
        const response = await get<Agent[]>('/api/users/agents');
        dispatch({ type: 'SET_AGENTS', agents: response });
      } catch (err) {
        console.error('Failed to fetch agents:', err);
        Sentry.captureException(err);
      }
    };
    fetchAgents();
  }, [user, agents.length, get, dispatch]);

  const toggleSelectNotice = useCallback((noticeId: number) => {
    setSelectedNoticeIds(prev => {
      const next = new Set(prev);
      if (next.has(noticeId)) {
        next.delete(noticeId);
      } else {
        next.add(noticeId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedNoticeIds(new Set()), []);

  useEffect(() => {
    clearSelection();
  }, [startDate, endDate, caseId, noCompany, subscriptionStatuses, refundedExternally, billableOnly, unbilled, companyId, brandId, assignedUserFilter, page, rowsPerPage, clearSelection]);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPage(0);
  };

  const handleChangePage = async (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleResolve = useCallback(async (success: boolean = true, message?: string) => {
    await fetchNotices();
    if (success) {
      setSnackbar({
        open: true,
        message: message || 'Notice resolved successfully',
        severity: 'success',
      });
    } else if (message) {
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  }, [fetchNotices]);

  const pageNotices = useMemo(() => noticesData?.notices ?? [], [noticesData]);
  const selectableNotices = useMemo(() => pageNotices.filter(isNoticeAssignable), [pageNotices]);
  const allOnPageSelected = selectableNotices.length > 0 && selectableNotices.every(n => selectedNoticeIds.has(n.verifi_notice_id));
  const someOnPageSelected = selectableNotices.some(n => selectedNoticeIds.has(n.verifi_notice_id)) && !allOnPageSelected;

  const handleSelectAll = () => {
    setSelectedNoticeIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        selectableNotices.forEach(n => next.delete(n.verifi_notice_id));
      } else {
        selectableNotices.forEach(n => next.add(n.verifi_notice_id));
      }
      return next;
    });
  };

  const alreadyAssignedCount = useMemo(
    () => pageNotices.filter(n => selectedNoticeIds.has(n.verifi_notice_id) && n.assigned_user_id).length,
    [pageNotices, selectedNoticeIds],
  );

  const handleOpenBulkAssign = () => {
    setBulkAssignAgent(null);
    setBulkAssignError(null);
    setBulkAssignDialogOpen(true);
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignAgent) return;
    setBulkAssignLoading(true);
    setBulkAssignError(null);
    try {
      const count = selectedNoticeIds.size;
      await post('/api/alerts/verifi-notices/assign', {
        noticeIds: [...selectedNoticeIds],
        userId: bulkAssignAgent,
      });
      await fetchNotices();
      clearSelection();
      setBulkAssignDialogOpen(false);
      const agent = agents.find(a => a.userId === bulkAssignAgent);
      setSnackbar({
        open: true,
        message: `${count} notice${count !== 1 ? 's' : ''} assigned to ${agent?.name ?? 'user'}`,
        severity: 'success',
      });
    } catch (err) {
      setBulkAssignError(err instanceof Error ? err.message : 'Failed to assign notices');
      Sentry.captureException(err);
    } finally {
      setBulkAssignLoading(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Alerts - RDR Alerts"
        description="Chargemont | Alerts - RDR Alerts"
      />
      <PageContainer>
        <PageHeader
          title="RDR Alerts"
          action={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <HasPermission permission={PERMISSIONS.ALERT_MERCHANT_TO_COMPANY}>
                <Badge
                  badgeContent={unmappedMerchants.length}
                  color="error"
                  max={99}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setMapMerchantDialogOpen(true)}
                    sx={{
                      borderColor: '#465fff',
                      color: '#465fff',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#3641f5',
                        backgroundColor: 'rgba(70, 95, 255, 0.04)',
                      },
                    }}
                  >
                    Link Merchant to Company
                  </Button>
                </Badge>
              </HasPermission>
              <HasPermission permission={PERMISSIONS.ALERTS_CSV}>
                <Button
                  variant="contained"
                  onClick={() => setCsvDialogOpen(true)}
                  sx={{
                    backgroundColor: '#465fff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3641f5',
                    },
                  }}
                >
                  Export CSV
                </Button>
              </HasPermission>
            </Box>
          }
        />

        {/* Filters */}
        <Box
          className="mb-6"
          sx={filterPanelSx}
        >
          {/* First Row: Date Pickers + CM Status Tabs + Filter Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    —
                  </Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Right side: Subscription Status filter + Filter Toggle */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={billableOnly}
                    onChange={(e) => { setBillableOnly(e.target.checked); setPage(0); }}
                  />
                }
                label="Billable only"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={unbilled}
                    onChange={(e) => { setUnbilled(e.target.checked); setPage(0); }}
                  />
                }
                label="Unbilled only"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <MultiSelectFilter
                label="Subscription Status"
                options={SUBSCRIPTION_STATUS_FILTER_OPTIONS}
                value={subscriptionStatuses}
                onChange={(values) => {
                  setSubscriptionStatuses(values);
                  setPage(0);
                }}
              />
              <MultiSelectFilter
                label="Refunded Externally"
                options={REFUNDED_EXTERNALLY_FILTER_OPTIONS}
                value={refundedExternally}
                onChange={(values) => {
                  setRefundedExternally(values);
                  setPage(0);
                }}
              />
              <SearchableMultiSelectFilter
                label="Assigned To"
                options={assignedUserFilterOptions}
                value={assignedUserFilter}
                placeholder="All"
                searchPlaceholder="Search agents…"
                onChange={(values) => {
                  setAssignedUserFilter(values);
                  setPage(0);
                }}
              />
              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                aria-label="Toggle advanced filters"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }}
                  />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Second Row: Filter Row (collapsible) */}
          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{
                px: 2,
                pb: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {companyOptions.length > 0 && (
                <SearchableMultiSelectFilter
                  label="Company"
                  options={companyOptions}
                  value={companyIds}
                  width={200}
                  searchPlaceholder="Search companies…"
                  showSelectAllClearAll
                  onChange={(values) => { setCompanyIds(values); setPage(0); }}
                />
              )}
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={200}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <SearchableMultiSelectFilter
                label="Descriptor"
                options={descriptorOptions}
                value={descriptorIds}
                width={200}
                searchPlaceholder="Search descriptors…"
                showSelectAllClearAll
                onChange={(values) => { setDescriptorIds(values); setPage(0); }}
              />
              <MultiSelectFilter
                label="Case Event"
                options={RDR_CASE_EVENT_FILTER_OPTIONS}
                value={caseEvents}
                onChange={(values) => {
                  setCaseEvents(values);
                  setPage(0);
                }}
              />
              <MultiSelectFilter
                label="Outcome"
                options={OUTCOME_FILTER_OPTIONS}
                value={outcomes}
                onChange={(values) => {
                  setOutcomes(values);
                  setPage(0);
                }}
              />
              <TextField
                size="small"
                label="Case ID"
                value={caseId}
                onChange={(e) => {
                  setCaseId(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
                placeholder="Search..."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noCompany}
                    onChange={(e) => { setNoCompany(e.target.checked); setPage(0); }}
                  />
                }
                label="Without company"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Bulk selection bar */}
        {hasAssignPermission && selectedNoticeIds.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(70, 95, 255, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedNoticeIds.size} selected
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleOpenBulkAssign}
              sx={{ backgroundColor: '#465fff', textTransform: 'none', '&:hover': { backgroundColor: '#3641f5' } }}
            >
              Assign selected
            </Button>
            <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {hasAssignPermission && (
                      <Checkbox
                        indeterminate={someOnPageSelected}
                        checked={allOnPageSelected}
                        onChange={handleSelectAll}
                        size="small"
                      />
                    )}
                  </TableCell>
                  {allColumns.filter(c => visibleColumns.includes(c.id)).map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortField ? (
                        <TableSortLabel
                          active={sortBy === column.sortField}
                          direction={sortBy === column.sortField ? sortOrder : 'asc'}
                          onClick={() => handleSort(column.sortField!)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : noticesData?.notices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No RDR notices found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  noticesData?.notices.map((notice) => (
                    <RdrNoticeRow key={notice.verifi_notice_id} notice={notice} onResolve={handleResolve} visibleColumns={visibleColumns} selectionEnabled={hasAssignPermission} selected={selectedNoticeIds.has(notice.verifi_notice_id)} onToggleSelect={toggleSelectNotice} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {noticesData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={noticesData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Columns
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              dense
              disabled={column.locked}
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                disabled={column.locked}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">
            Reset to Default
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleShowAllColumns} dense>
          <Typography variant="body2" color="primary">
            All Columns
          </Typography>
        </MenuItem>
      </Menu>

      {/* Bulk Assign Dialog */}
      <Dialog
        open={bulkAssignDialogOpen}
        onClose={() => { if (!bulkAssignLoading) setBulkAssignDialogOpen(false); }}
        aria-labelledby="bulk-assign-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="bulk-assign-dialog-title">Assign Notices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign {selectedNoticeIds.size} notice{selectedNoticeIds.size !== 1 ? 's' : ''} to:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-assign-agent-label">Select User</InputLabel>
            <Select
              labelId="bulk-assign-agent-label"
              value={bulkAssignAgent ?? ''}
              label="Select User"
              onChange={(e) => setBulkAssignAgent(e.target.value ? Number(e.target.value) : null)}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.userId} value={agent.userId}>
                  {agent.name} ({agent.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {alreadyAssignedCount > 0 && (
            <MuiAlert severity="warning" sx={{ mt: 2 }}>
              {alreadyAssignedCount} of {selectedNoticeIds.size} notice{selectedNoticeIds.size !== 1 ? 's' : ''} {alreadyAssignedCount === 1 ? 'is' : 'are'} already assigned and will be reassigned.
            </MuiAlert>
          )}
          {bulkAssignError && (
            <MuiAlert severity="error" sx={{ mt: 2 }}>
              {bulkAssignError}
            </MuiAlert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkAssignDialogOpen(false)} color="inherit" disabled={bulkAssignLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={!bulkAssignAgent || bulkAssignLoading}
            sx={{ backgroundColor: '#465fff', textTransform: 'none', '&:hover': { backgroundColor: '#3641f5' } }}
          >
            {bulkAssignLoading ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <VerifiNoticesCsvDialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} companyId={companyId} />
      <MapMerchantToCompanyDialog
        open={mapMerchantDialogOpen}
        onClose={() => setMapMerchantDialogOpen(false)}
        onSuccess={() => {
          fetchNotices();
          refreshUnmappedMerchants();
        }}
        provider={RDR_PROVIDER_CONFIG}
        merchants={unmappedMerchants}
      />
    </div>
  );
}
